import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
  Stack,
  TextField,
  Typography,
  Zoom,
  styled,
} from "@mui/material";
import { useNewsletterEditor } from "../context/NewsletterEditorContext";
import { findNodeById, findRowById } from "../model/documentModel";
import { getColumnSlotState } from "../model/columnContentUtils";
import { TEXT_COMPONENT_KEYS } from "./ComponentVisualPreview";
import { buildSavedComponentPayload, extractLayoutBlockForSave } from "../model/documentMutations";
import ColorPickerField from "../fields/ColorPickerField";
import PropertySection from "../fields/PropertySection";
import BorderEditor from "../fields/BorderEditor";
import ConfirmDialog from "../dialogs/ConfirmDialog";
import HtmlEditorDialog from "../dialogs/HtmlEditorDialog";
import AddGridDialog from "../dialogs/AddGridDialog";
import { listNewsletterImages, saveNewsletterComponent, saveNewsletterLayout } from "../../../../services/newsletterStorageService";
import { formatStorageDisplayName } from "../utils/storageDisplayUtils";
import { SelectPropertyField, SliderPropertyField } from "../fields/PropertyFieldControls";
import { buildEditorBreadcrumb } from "../utils/selectionBreadcrumbUtils";
import { DEFAULT_LAYOUT_MARGINS } from "../utils/layoutStyleUtils";
import {
  FONT_SIZE_MAX_PX,
  FONT_SIZE_MIN_PX,
  LINK_TARGET_OPTIONS,
  TEXT_ALIGN_OPTIONS,
  VERTICAL_ALIGN_OPTIONS,
  formatPx,
  parseFontWeight,
  parsePercent,
  parsePx,
} from "../utils/editorFieldUtils";
import { EDITOR_SLIDER_LIMITS } from "../../../../config/newsletterEditorConfig";
import { EDITOR_ACCENT } from "../editorUiConstants";

const UnifiedDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "16px",
    padding: theme.spacing(2),
    boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
  },
}));

const ReadOnlyField = ({ label, value }) => (
  <TextField fullWidth size="small" label={label} value={value || "—"} InputProps={{ readOnly: true }} sx={{ mb: 1.5 }} />
);

ReadOnlyField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
};

const PropertiesBreadcrumb = ({ segments, onSelectSegment }) => {
  if (!segments?.length) {
    return null;
  }
  return (
    <Typography variant="caption" component="div" sx={{ color: "#64748b", mb: 2, lineHeight: 1.5 }}>
      {segments.map((segment, index) => (
        <React.Fragment key={`${segment.id ?? "newsletter"}-${index}`}>
          {index > 0 ? (
            <Box component="span" sx={{ mx: 0.5, color: "#94a3b8" }}>
              ›
            </Box>
          ) : null}
          <Box
            component="button"
            type="button"
            onClick={() => onSelectSegment?.(segment)}
            sx={{
              border: "none",
              p: 0,
              m: 0,
              background: "none",
              font: "inherit",
              color: segment.active ? EDITOR_ACCENT : "#64748b",
              fontWeight: segment.active ? 700 : 500,
              cursor: onSelectSegment ? "pointer" : "default",
              textDecoration: onSelectSegment && !segment.active ? "underline" : "none",
              textUnderlineOffset: "2px",
              "&:hover": onSelectSegment
                ? {
                    color: EDITOR_ACCENT,
                  }
                : undefined,
            }}
          >
            {segment.label}
          </Box>
        </React.Fragment>
      ))}
    </Typography>
  );
};

PropertiesBreadcrumb.propTypes = {
  segments: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      id: PropTypes.string,
      nodeType: PropTypes.string,
      active: PropTypes.bool,
    })
  ),
  onSelectSegment: PropTypes.func,
};

const formatUpdatedAt = (value) => {
  if (!value) {
    return "—";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString("it-IT");
};

const DocumentGeneralProperties = () => {
  const { document, replaceMeta } = useNewsletterEditor();
  return (
    <PropertySection title="Newsletter">
      <TextField
        fullWidth
        size="small"
        label="Nome"
        value={document.name || ""}
        onChange={(e) => replaceMeta({ name: e.target.value })}
        sx={{ mb: 1.5 }}
      />
      <TextField
        fullWidth
        size="small"
        label="Oggetto email"
        placeholder="Oggetto della email inviata ai destinatari"
        value={document.subject || ""}
        onChange={(e) => replaceMeta({ subject: e.target.value })}
        sx={{ mb: 1.5 }}
      />
      <ReadOnlyField label="Ultimo aggiornamento" value={formatUpdatedAt(document.updatedAt)} />
    </PropertySection>
  );
};

const removeMessageByType = {
  component: "Rimuovere il componente selezionato?",
  grid: "Rimuovere il layout selezionato e tutto il suo contenuto?",
};

const PropertiesPanel = () => {
  const {
    document,
    selection,
    setSelection,
    updateProps,
    updateGridName,
    removeSelected,
    clearColumnContent,
    addNestedGrid,
    setColumnWidth,
    resizeGrid,
    moveLayoutGrid,
    showEditorMessage,
    imagesRevision,
    requestInlineTextEdit,
    publishSavedComponent,
  } = useNewsletterEditor();

  const [images, setImages] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saveComponentOpen, setSaveComponentOpen] = useState(false);
  const [saveLayoutOpen, setSaveLayoutOpen] = useState(false);
  const [nestedGridOpen, setNestedGridOpen] = useState(false);
  const [resizeGridOpen, setResizeGridOpen] = useState(false);
  const [componentName, setComponentName] = useState("");
  const [layoutSaveName, setLayoutSaveName] = useState("");
  const [htmlEditorOpen, setHtmlEditorOpen] = useState(false);

  useEffect(() => {
    listNewsletterImages()
      .then(setImages)
      .catch((err) => {
        setImages([]);
        showEditorMessage({
          title: "Errore caricamento immagini",
          message: err.message,
        });
      });
  }, [imagesRevision, showEditorMessage]);

  const selected = useMemo(() => {
    if (!selection?.id) {
      return null;
    }
    return findNodeById(document.layout, selection.id);
  }, [document.layout, selection]);

  const selectedNode = selected?.node;
  const nodeType = selected?.nodeType;
  const isComponent = nodeType === "component";
  const isGrid = nodeType === "grid";
  const isColumn = nodeType === "column";

  const patchProps = (patch, asComponent = isComponent, options = {}) => {
    if (!selectedNode) {
      return;
    }
    updateProps(selectedNode.id, patch, asComponent, options);
  };

  const patchPropsLive = (patch, asComponent = isComponent) => {
    patchProps(patch, asComponent, { recordHistory: false });
  };

  const patchPropsCommit = (patch, asComponent = isComponent) => {
    patchProps(patch, asComponent, { recordHistory: true });
  };

  const patchGridMargins = (side, value, { recordHistory = true } = {}) => {
    const margins = { ...DEFAULT_LAYOUT_MARGINS, ...(selectedNode.props?.margins || {}), [side]: value };
    patchProps({ margins }, false, { recordHistory });
  };

  const rowContext =
    isColumn && selection?.rowId ? findRowById(document.layout, selection.rowId) : null;

  const columnSlot = isColumn && selectedNode ? getColumnSlotState(selectedNode) : null;

  const breadcrumbSegments = useMemo(() => {
    const newsletterSegment = {
      label: "Newsletter",
      id: null,
      nodeType: "document",
      active: !selection?.id,
    };
    if (!selection?.id) {
      return [newsletterSegment];
    }
    const path = buildEditorBreadcrumb(document.layout, selection.id);
    return [
      newsletterSegment,
      ...path.map((segment) => ({
        ...segment,
        active: segment.id === selection.id,
      })),
    ];
  }, [document.layout, selection?.id]);

  const handleBreadcrumbSelect = (segment) => {
    if (segment.nodeType === "document") {
      setSelection(null);
      return;
    }
    if (segment.nodeType === "column") {
      setSelection({
        type: "column",
        id: segment.id,
        rowId: segment.rowId,
        gridId: segment.gridId,
      });
      return;
    }
    if (segment.nodeType === "grid") {
      setSelection({ type: "grid", id: segment.id });
      return;
    }
    if (segment.nodeType === "component") {
      setSelection({ type: "component", id: segment.id });
    }
  };

  const isTopLevelGrid = isGrid && selected?.parent?.type === "layout";
  const topLevelGridIndex = isTopLevelGrid
    ? (document.layout?.grids || []).findIndex((grid) => grid.id === selectedNode?.id)
    : -1;
  const topLevelGridCount = document.layout?.grids?.length ?? 0;

  const renderComponentPositionFields = () => (
    <PropertySection title="Posizione in cella">
      <SelectPropertyField
        label="Orizzontale"
        value={selectedNode.props?.contentAlign || selectedNode.props?.align || "center"}
        options={TEXT_ALIGN_OPTIONS}
        onChange={(value) => patchProps({ contentAlign: value }, true)}
      />
      <SelectPropertyField
        label="Verticale"
        value={selectedNode.props?.contentVAlign || "center"}
        options={VERTICAL_ALIGN_OPTIONS}
        onChange={(value) => patchProps({ contentVAlign: value }, true)}
      />
    </PropertySection>
  );

  const renderImageProperties = () => (
    <>
      <PropertySection title="Sorgente immagine">
        <TextField
          fullWidth
          size="small"
          label="URL immagine"
          placeholder="https://..."
          value={selectedNode.props?.src || ""}
          onChange={(e) => patchProps({ src: e.target.value }, true)}
          sx={{ mb: 1.5 }}
        />
        <SelectPropertyField
          label="Libreria immagini"
          value={
            images.some((image) => image.publicUrl === selectedNode.props?.src)
              ? selectedNode.props?.src
              : ""
          }
          options={[
            { value: "", label: "Seleziona dalla libreria..." },
            ...images.map((image) => ({
              value: image.publicUrl,
              label: formatStorageDisplayName(image, "image"),
            })),
          ]}
          onChange={(value) => {
            const image = images.find((item) => item.publicUrl === value);
            if (image) {
              patchProps(
                {
                  src: image.publicUrl,
                  alt: formatStorageDisplayName(image, "image"),
                  widthPercent: selectedNode.props?.widthPercent ?? 100,
                },
                true
              );
            }
          }}
        />
      </PropertySection>
      <PropertySection title="Dimensioni">
        <SliderPropertyField
          label="Larghezza"
          value={parsePercent(selectedNode.props?.widthPercent ?? selectedNode.props?.width, 100)}
          min={EDITOR_SLIDER_LIMITS.imageWidthPercent.min}
          max={EDITOR_SLIDER_LIMITS.imageWidthPercent.max}
          unit="%"
          onChange={(val) => patchPropsLive({ widthPercent: val }, true)}
          onChangeCommitted={(val) => patchPropsCommit({ widthPercent: val }, true)}
        />
        <SliderPropertyField
          label="Larghezza fissa (px, 0 = auto)"
          value={selectedNode.props?.fixedWidthPx ?? 0}
          min={EDITOR_SLIDER_LIMITS.imageFixedWidthPx.min}
          max={EDITOR_SLIDER_LIMITS.imageFixedWidthPx.max}
          unit="px"
          onChange={(val) => patchPropsLive({ fixedWidthPx: val }, true)}
          onChangeCommitted={(val) => patchPropsCommit({ fixedWidthPx: val }, true)}
        />
        <SliderPropertyField
          label="Altezza fissa (px, 0 = auto)"
          value={selectedNode.props?.fixedHeightPx ?? 0}
          min={EDITOR_SLIDER_LIMITS.imageFixedHeightPx.min}
          max={EDITOR_SLIDER_LIMITS.imageFixedHeightPx.max}
          unit="px"
          onChange={(val) => patchPropsLive({ fixedHeightPx: val }, true)}
          onChangeCommitted={(val) => patchPropsCommit({ fixedHeightPx: val }, true)}
        />
      </PropertySection>
      <PropertySection title="Collegamento">
        <SelectPropertyField
          label="Link destinazione"
          value={selectedNode.props?.href || ""}
          options={LINK_TARGET_OPTIONS}
          onChange={(value) => patchProps({ href: value }, true)}
        />
      </PropertySection>
      {renderComponentPositionFields()}
      <PropertySection title="Aspetto">
        <BorderEditor
          label="Bordo immagine"
          value={selectedNode.props?.border}
          onChange={(border) => patchProps({ border }, true)}
        />
      </PropertySection>
    </>
  );

  const renderButtonProperties = () => (
    <>
      <PropertySection title="Contenuto">
        <Button
          size="small"
          variant="outlined"
          sx={{ mb: 0, borderRadius: 0 }}
          onClick={() => requestInlineTextEdit(selectedNode.id)}
        >
          Modifica etichetta
        </Button>
      </PropertySection>
      <PropertySection title="Collegamento">
        <TextField
          fullWidth
          size="small"
          label="Link destinazione"
          placeholder="https://..."
          value={selectedNode.props?.href || ""}
          onChange={(e) => patchProps({ href: e.target.value }, true)}
          sx={{ mb: 1.5 }}
        />
      </PropertySection>
      <PropertySection title="Colori">
        <ColorPickerField
          label="Sfondo"
          value={selectedNode.props?.backgroundColor}
          defaultColor="#0b3d87"
          onChange={(value) => patchProps({ backgroundColor: value }, true)}
        />
        <ColorPickerField
          label="Testo"
          value={selectedNode.props?.color}
          defaultColor="#ffffff"
          onChange={(value) => patchProps({ color: value }, true)}
        />
        <SelectPropertyField
          label="Allineamento testo"
          value={selectedNode.props?.align || "center"}
          options={TEXT_ALIGN_OPTIONS}
          onChange={(value) => patchProps({ align: value }, true)}
        />
      </PropertySection>
      <PropertySection title="Dimensioni">
        <SliderPropertyField
          label="Larghezza"
          value={selectedNode.props?.buttonWidthPercent ?? 0}
          min={EDITOR_SLIDER_LIMITS.buttonWidthPercent.min}
          max={EDITOR_SLIDER_LIMITS.buttonWidthPercent.max}
          unit="%"
          onChange={(val) => patchPropsLive({ buttonWidthPercent: val }, true)}
          onChangeCommitted={(val) => patchPropsCommit({ buttonWidthPercent: val }, true)}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
          0% = larghezza automatica in base al testo
        </Typography>
        <SliderPropertyField
          label="Padding orizzontale"
          value={selectedNode.props?.buttonPaddingX ?? 24}
          min={EDITOR_SLIDER_LIMITS.buttonPaddingXPx.min}
          max={EDITOR_SLIDER_LIMITS.buttonPaddingXPx.max}
          unit="px"
          onChange={(val) => patchPropsLive({ buttonPaddingX: val }, true)}
          onChangeCommitted={(val) => patchPropsCommit({ buttonPaddingX: val }, true)}
        />
        <SliderPropertyField
          label="Padding verticale"
          value={selectedNode.props?.buttonPaddingY ?? 12}
          min={EDITOR_SLIDER_LIMITS.buttonPaddingYPx.min}
          max={EDITOR_SLIDER_LIMITS.buttonPaddingYPx.max}
          unit="px"
          onChange={(val) => patchPropsLive({ buttonPaddingY: val }, true)}
          onChangeCommitted={(val) => patchPropsCommit({ buttonPaddingY: val }, true)}
        />
      </PropertySection>
      {renderComponentPositionFields()}
      <PropertySection title="Aspetto">
        <BorderEditor
          label="Bordo bottone"
          value={selectedNode.props?.border}
          onChange={(border) => patchProps({ border }, true)}
        />
      </PropertySection>
    </>
  );

  const renderTextProperties = () => (
    <>
      <PropertySection title="Contenuto">
        <Button
          size="small"
          variant="outlined"
          sx={{ borderRadius: 0 }}
          onClick={() => requestInlineTextEdit(selectedNode.id)}
        >
          Modifica testo
        </Button>
      </PropertySection>
      <PropertySection title="Tipografia">
        <SelectPropertyField
          label="Allineamento"
          value={selectedNode.props?.align || "left"}
          options={TEXT_ALIGN_OPTIONS}
          onChange={(value) => patchProps({ align: value }, true)}
        />
        <ColorPickerField
          label="Colore testo"
          value={selectedNode.props?.color}
          defaultColor="#333333"
          onChange={(value) => patchProps({ color: value }, true)}
        />
        <SliderPropertyField
          label="Dimensione font"
          value={parsePx(selectedNode.props?.fontSize, 16)}
          min={FONT_SIZE_MIN_PX}
          max={FONT_SIZE_MAX_PX}
          unit="px"
          onChange={(val) => patchPropsLive({ fontSize: formatPx(val) }, true)}
          onChangeCommitted={(val) => patchPropsCommit({ fontSize: formatPx(val) }, true)}
        />
        <SliderPropertyField
          label="Peso font"
          value={parseFontWeight(selectedNode.props?.fontWeight, 400)}
          min={100}
          max={900}
          step={100}
          onChange={(val) => patchPropsLive({ fontWeight: String(val) }, true)}
          onChangeCommitted={(val) => patchPropsCommit({ fontWeight: String(val) }, true)}
        />
      </PropertySection>
      <PropertySection title="Aspetto">
        <ColorPickerField
          label="Sfondo"
          value={selectedNode.props?.backgroundColor}
          defaultColor="#ffffff"
          allowClear
          onChange={(value) => patchProps({ backgroundColor: value }, true)}
        />
        <BorderEditor
          label="Bordo"
          value={selectedNode.props?.border}
          onChange={(border) => patchProps({ border }, true)}
        />
      </PropertySection>
    </>
  );

  const renderColumnProperties = () => (
    <>
      <PropertySection title="Struttura">
        {rowContext?.row?.columns?.length > 1 ? (
          <>
            <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
              Larghezza colonna: {selectedNode.widthPercent}%
            </Typography>
            <Slider
              size="small"
              min={EDITOR_SLIDER_LIMITS.columnWidthPercent.min}
              max={EDITOR_SLIDER_LIMITS.columnWidthPercent.max}
              value={selectedNode.widthPercent ?? 50}
              onChange={(_, val) => setColumnWidth(rowContext.row.id, selectedNode.id, val)}
              sx={{ mb: 1.5 }}
            />
          </>
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
            Larghezza colonna: 100%
          </Typography>
        )}
        <Button
          size="small"
          sx={{ borderRadius: 0 }}
          disabled={columnSlot?.hasComponent || columnSlot?.hasNestedGrid}
          onClick={() => {
            if (columnSlot?.hasComponent) {
              showEditorMessage({
                title: "Layout interno non disponibile",
                message: "Rimuovi il componente dalla cella prima di aggiungere un layout interno.",
              });
              return;
            }
            setNestedGridOpen(true);
          }}
        >
          Aggiungi layout interno
        </Button>
      </PropertySection>
      <PropertySection title="Spaziatura interna">
        <SliderPropertyField
          label="Padding"
          value={parsePx(selectedNode.props?.padding, 0)}
          min={EDITOR_SLIDER_LIMITS.spacingPx.min}
          max={EDITOR_SLIDER_LIMITS.spacingPx.max}
          unit="px"
          onChange={(val) => patchPropsLive({ padding: formatPx(val) })}
          onChangeCommitted={(val) => patchPropsCommit({ padding: formatPx(val) })}
        />
      </PropertySection>
      <PropertySection title="Allineamento contenuto">
        <SelectPropertyField
          label="Verticale"
          value={selectedNode.props?.contentVAlign || "top"}
          options={VERTICAL_ALIGN_OPTIONS}
          onChange={(value) => patchProps({ contentVAlign: value })}
        />
      </PropertySection>
      <PropertySection title="Aspetto">
        <ColorPickerField
          label="Sfondo"
          value={selectedNode.props?.backgroundColor}
          defaultColor="#ffffff"
          allowClear
          onChange={(value) => patchProps({ backgroundColor: value })}
        />
        <BorderEditor value={selectedNode.props?.border} onChange={(border) => patchProps({ border })} />
      </PropertySection>
    </>
  );

  const renderGridProperties = () => (
    <>
      <TextField
        fullWidth
        size="small"
        label="Nome layout"
        value={selectedNode.name || ""}
        onChange={(e) => updateGridName(selectedNode.id, e.target.value)}
        sx={{ mb: 2 }}
      />
      <PropertySection title="Griglia">
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          Righe: {selectedNode.rows?.length ?? 0} — Colonne: {selectedNode.rows?.[0]?.columns?.length ?? 0}
        </Typography>
        <Button size="small" sx={{ mb: 1.5, borderRadius: 0 }} onClick={() => setResizeGridOpen(true)}>
          Modifica righe e colonne
        </Button>
        <SliderPropertyField
          label="Spazio tra celle"
          value={parsePx(selectedNode.props?.cellGap, 0)}
          min={EDITOR_SLIDER_LIMITS.cellGapPx.min}
          max={EDITOR_SLIDER_LIMITS.cellGapPx.max}
          unit="px"
          onChange={(val) => patchPropsLive({ cellGap: val })}
          onChangeCommitted={(val) => patchPropsCommit({ cellGap: val })}
        />
      </PropertySection>
      <PropertySection title="Dimensioni e posizione">
        <SliderPropertyField
          label="Larghezza layout"
          value={selectedNode.props?.widthPercent ?? 100}
          min={EDITOR_SLIDER_LIMITS.layoutWidthPercent.min}
          max={EDITOR_SLIDER_LIMITS.layoutWidthPercent.max}
          unit="%"
          onChange={(val) => patchPropsLive({ widthPercent: val })}
          onChangeCommitted={(val) => patchPropsCommit({ widthPercent: val })}
        />
        <SelectPropertyField
          label="Allineamento orizzontale"
          value={selectedNode.props?.contentAlign || "left"}
          options={TEXT_ALIGN_OPTIONS}
          onChange={(value) => patchProps({ contentAlign: value })}
        />
        <SliderPropertyField
          label="Margine sopra"
          value={selectedNode.props?.margins?.top ?? 0}
          min={EDITOR_SLIDER_LIMITS.layoutMarginPx.min}
          max={EDITOR_SLIDER_LIMITS.layoutMarginPx.max}
          unit="px"
          onChange={(val) => patchGridMargins("top", val, { recordHistory: false })}
          onChangeCommitted={(val) => patchGridMargins("top", val, { recordHistory: true })}
        />
        <SliderPropertyField
          label="Margine destra"
          value={selectedNode.props?.margins?.right ?? 0}
          min={EDITOR_SLIDER_LIMITS.layoutMarginPx.min}
          max={EDITOR_SLIDER_LIMITS.layoutMarginPx.max}
          unit="px"
          onChange={(val) => patchGridMargins("right", val, { recordHistory: false })}
          onChangeCommitted={(val) => patchGridMargins("right", val, { recordHistory: true })}
        />
        <SliderPropertyField
          label="Margine sotto"
          value={selectedNode.props?.margins?.bottom ?? 0}
          min={EDITOR_SLIDER_LIMITS.layoutMarginPx.min}
          max={EDITOR_SLIDER_LIMITS.layoutMarginPx.max}
          unit="px"
          onChange={(val) => patchGridMargins("bottom", val, { recordHistory: false })}
          onChangeCommitted={(val) => patchGridMargins("bottom", val, { recordHistory: true })}
        />
        <SliderPropertyField
          label="Margine sinistra"
          value={selectedNode.props?.margins?.left ?? 0}
          min={EDITOR_SLIDER_LIMITS.layoutMarginPx.min}
          max={EDITOR_SLIDER_LIMITS.layoutMarginPx.max}
          unit="px"
          onChange={(val) => patchGridMargins("left", val, { recordHistory: false })}
          onChangeCommitted={(val) => patchGridMargins("left", val, { recordHistory: true })}
        />
      </PropertySection>
      <PropertySection title="Aspetto">
        <SliderPropertyField
          label="Padding interno"
          value={parsePx(selectedNode.props?.padding, 0)}
          min={EDITOR_SLIDER_LIMITS.spacingPx.min}
          max={EDITOR_SLIDER_LIMITS.spacingPx.max}
          unit="px"
          onChange={(val) => patchPropsLive({ padding: formatPx(val) })}
          onChangeCommitted={(val) => patchPropsCommit({ padding: formatPx(val) })}
        />
        <ColorPickerField
          label="Sfondo"
          value={selectedNode.props?.backgroundColor}
          defaultColor="#ffffff"
          allowClear
          onChange={(value) => patchProps({ backgroundColor: value })}
        />
        <BorderEditor
          label="Bordo layout"
          value={selectedNode.props?.border}
          onChange={(border) => patchProps({ border })}
        />
      </PropertySection>
      <PropertySection title="Azioni">
        <Button
          size="small"
          variant="outlined"
          sx={{ borderRadius: 0, mb: isTopLevelGrid && topLevelGridCount > 1 ? 1 : 0 }}
          onClick={() => {
            setLayoutSaveName(selectedNode.name || "");
            setSaveLayoutOpen(true);
          }}
        >
          Salva layout
        </Button>
        {isTopLevelGrid && topLevelGridCount > 1 ? (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              disabled={topLevelGridIndex <= 0}
              sx={{ borderRadius: 0, flex: 1 }}
              onClick={() => moveLayoutGrid(selectedNode.id, -1)}
            >
              Sposta su
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={topLevelGridIndex < 0 || topLevelGridIndex >= topLevelGridCount - 1}
              sx={{ borderRadius: 0, flex: 1 }}
              onClick={() => moveLayoutGrid(selectedNode.id, 1)}
            >
              Sposta giù
            </Button>
          </Stack>
        ) : null}
      </PropertySection>
    </>
  );

  const handleSaveComponent = async () => {
    if (!isComponent || !selectedNode) {
      return;
    }
    if (!componentName.trim()) {
      showEditorMessage({
        title: "Nome obbligatorio",
        message: "Inserisci un nome per il componente da salvare.",
      });
      return;
    }
    try {
      const payload = buildSavedComponentPayload(componentName.trim(), selectedNode);
      const summary = await saveNewsletterComponent(payload);
      publishSavedComponent(summary, payload);
      setSaveComponentOpen(false);
      setComponentName("");
    } catch (err) {
      showEditorMessage({
        title: "Salvataggio componente non riuscito",
        message: err.message,
      });
    }
  };

  const handleSaveLayout = async () => {
    if (!isGrid || !selectedNode) {
      return;
    }
    if (!layoutSaveName.trim()) {
      showEditorMessage({
        title: "Nome obbligatorio",
        message: "Inserisci un nome per il layout da salvare.",
      });
      return;
    }
    try {
      await saveNewsletterLayout(extractLayoutBlockForSave(selectedNode), layoutSaveName.trim());
      setSaveLayoutOpen(false);
      setLayoutSaveName("");
    } catch (err) {
      showEditorMessage({
        title: "Salvataggio layout non riuscito",
        message: err.message,
      });
    }
  };

  const renderComponentProperties = () => {
    if (selectedNode.componentKey === "plain-html") {
      return (
        <PropertySection title="HTML">
          <Button size="small" variant="outlined" sx={{ borderRadius: 0 }} onClick={() => setHtmlEditorOpen(true)}>
            Modifica HTML
          </Button>
        </PropertySection>
      );
    }
    if (selectedNode.componentKey === "image") {
      return renderImageProperties();
    }
    if (selectedNode.componentKey === "button") {
      return renderButtonProperties();
    }
    if (TEXT_COMPONENT_KEYS.includes(selectedNode.componentKey)) {
      return renderTextProperties();
    }
    return null;
  };

  return (
    <Box
      sx={{
        border: "1px solid #e2e8f0",
        borderRadius: 2,
        backgroundColor: "#fff",
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          p: 2,
          boxSizing: "border-box",
          maxWidth: "100%",
          "& .MuiSlider-root": {
            maxWidth: "100%",
          },
        }}
      >
      <Typography variant="subtitle2" sx={{ color: EDITOR_ACCENT, fontWeight: 700, mb: 1 }}>
        Proprietà
      </Typography>

      <PropertiesBreadcrumb segments={breadcrumbSegments} onSelectSegment={handleBreadcrumbSelect} />

      {!selectedNode ? <DocumentGeneralProperties /> : null}

      {isComponent ? (
        <Stack spacing={0}>
          {renderComponentProperties()}
          <PropertySection title="" sx={{ borderBottom: "none" }}>
            <Button size="small" variant="outlined" sx={{ borderRadius: 0 }} onClick={() => setSaveComponentOpen(true)}>
              Salva come componente
            </Button>
          </PropertySection>
        </Stack>
      ) : null}

      {isColumn ? renderColumnProperties() : null}
      {isGrid ? renderGridProperties() : null}

      {isColumn ? (
        <Button
          color="error"
          size="small"
          sx={{ mt: 2, borderRadius: 0 }}
          disabled={!columnSlot?.hasComponent && !columnSlot?.hasNestedGrid}
          onClick={() => setConfirmOpen(true)}
        >
          Svuota cella
        </Button>
      ) : null}

      {selectedNode && !isColumn ? (
        <Button variant="outlined" color="error" size="small" sx={{ borderRadius: 0 }} onClick={() => setConfirmOpen(true)}>
          Rimuovi elemento
        </Button>
      ) : null}

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {
          if (isColumn) {
            clearColumnContent(selectedNode.id);
          } else {
            removeSelected(selectedNode.id);
          }
          setConfirmOpen(false);
        }}
        title={isColumn ? "Svuota cella" : "Rimuovi elemento"}
        message={
          isColumn
            ? "Rimuovere il contenuto della cella (componente o layout interno)? La cella resterà nella griglia."
            : removeMessageByType[nodeType] || "Rimuovere l'elemento selezionato?"
        }
        confirmColor="#b30047"
      />

      <AddGridDialog
        open={resizeGridOpen}
        onClose={() => setResizeGridOpen(false)}
        title="Modifica righe e colonne"
        confirmLabel="Applica"
        initialRows={selectedNode?.rows?.length || 1}
        initialColumns={selectedNode?.rows?.[0]?.columns?.length || 1}
        onConfirm={(rows, columns) => {
          resizeGrid(selectedNode.id, rows, columns);
          setResizeGridOpen(false);
        }}
      />

      <AddGridDialog
        open={nestedGridOpen}
        onClose={() => setNestedGridOpen(false)}
        title="Aggiungi layout interno"
        onConfirm={(rows, columns) => {
          addNestedGrid(selectedNode.id, rows, columns);
          setNestedGridOpen(false);
        }}
      />

      <UnifiedDialog open={saveComponentOpen} onClose={() => setSaveComponentOpen(false)} maxWidth="xs" fullWidth slots={{ transition: Zoom }}>
        <Box
          component="form"
          onSubmit={(event) => {
            event.preventDefault();
            handleSaveComponent();
          }}
        >
          <DialogTitle sx={{ textAlign: "center", color: EDITOR_ACCENT, fontWeight: 700 }}>Salva componente</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Nome componente"
              value={componentName}
              onChange={(e) => setComponentName(e.target.value)}
              autoFocus
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
            <Button type="button" onClick={() => setSaveComponentOpen(false)}>
              Annulla
            </Button>
            <Button type="submit" variant="contained">
              Salva
            </Button>
          </DialogActions>
        </Box>
      </UnifiedDialog>

      <UnifiedDialog open={saveLayoutOpen} onClose={() => setSaveLayoutOpen(false)} maxWidth="xs" fullWidth slots={{ transition: Zoom }}>
        <DialogTitle sx={{ textAlign: "center", color: EDITOR_ACCENT, fontWeight: 700 }}>Salva layout</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Nome layout"
            value={layoutSaveName}
            onChange={(e) => setLayoutSaveName(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
          <Button onClick={() => setSaveLayoutOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={handleSaveLayout}>
            Salva
          </Button>
        </DialogActions>
      </UnifiedDialog>

      <HtmlEditorDialog
        open={htmlEditorOpen}
        initialValue={selectedNode?.props?.html || ""}
        onClose={() => setHtmlEditorOpen(false)}
        onSave={(html) => {
          patchProps({ html }, true, { recordHistory: true });
          setHtmlEditorOpen(false);
        }}
      />
      </Box>
    </Box>
  );
};

export default PropertiesPanel;
 